<script setup lang="ts">
// A hold on the node's updates (DECISIONS 2026-10-17 "nodes behind
// central"), on the machine's Overview: before an event an editor holds the
// node on its version, until a time or until lifted, with why; meanwhile the
// node refuses any update from the interface (an upload too). Anyone who may
// change the node lifts it, a superadmin like everyone; it is recorded.
import { computed, ref } from 'vue'
import { useNode } from '../../store'
import { held, holdText } from '../../hold'
import ConfirmDialog from '../ConfirmDialog.vue'

const node = useNode()
const hold = computed(() => node.config?.update_hold)
const on = computed(() => held(hold.value, node.now))
const nodeName = computed(() => node.config?.node.name || 'this node')

const editing = ref(false) // the hold's form
const until = ref('') // datetime-local; empty: until lifted
const note = ref('')
const lifting = ref(false)
const busy = ref(false)
const err = ref('')

function open() {
    until.value = ''
    note.value = hold.value?.note ?? ''
    err.value = ''
    editing.value = true
}
async function save() {
    err.value = ''
    if (until.value && Date.parse(until.value) <= Date.now()) {
        err.value = 'Until is in the past: leave it empty to hold until lifted.'
        return
    }
    busy.value = true
    try {
        await node.setUpdateHold({
            until: until.value ? new Date(until.value).toISOString() : undefined,
            note: note.value.trim(),
        })
        editing.value = false
    } catch {
        /* toast shown by the store */
    } finally {
        busy.value = false
    }
}
async function lift() {
    lifting.value = false
    busy.value = true
    try {
        await node.setUpdateHold(null)
    } catch {
        /* toast shown by the store */
    } finally {
        busy.value = false
    }
}
</script>

<template>
    <div data-test="update-hold">
        <div
            class="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-2.5 py-1.5"
            :class="on ? 'border-warn/40 bg-warn/8' : 'border-line'"
        >
            <b class="text-[13px] font-semibold" :class="on ? 'text-warn' : ''">{{
                on ? 'Updates held' : 'Updates not held'
            }}</b>
            <span class="min-w-0 truncate text-xs text-fg-2" :title="on && hold ? holdText(hold) : undefined">{{
                on && hold ? holdText(hold) : 'the node software can be updated from the interface'
            }}</span>
            <template v-if="!node.readOnly && !editing">
                <button
                    v-if="on"
                    class="ml-auto text-xs"
                    :disabled="busy"
                    data-test="update-hold-lift"
                    @click="lifting = true"
                >
                    Lift…
                </button>
                <button
                    class="text-xs"
                    :class="{ 'ml-auto': !on }"
                    :disabled="busy"
                    data-test="update-hold-set"
                    @click="open"
                >
                    {{ on ? 'Change…' : 'Hold…' }}
                </button>
            </template>
        </div>
        <form v-if="editing" class="mt-1.5 grid gap-1.5 text-xs" data-test="update-hold-form" @submit.prevent="save">
            <label class="grid gap-0.5"
                >Until (empty: until somebody lifts it)
                <input v-model="until" type="datetime-local" data-test="update-hold-until"
            /></label>
            <label class="grid gap-0.5"
                >Why
                <input
                    v-model="note"
                    maxlength="300"
                    placeholder="the final, Sunday 20:00"
                    data-test="update-hold-note"
            /></label>
            <div v-if="err" class="text-bad" role="alert">{{ err }}</div>
            <div class="flex gap-2">
                <button class="btn-primary text-xs" type="submit" :disabled="busy">Hold updates</button>
                <button class="btn-ghost text-xs" type="button" @click="editing = false">Cancel</button>
            </div>
        </form>
        <p class="mt-1.5 text-xs text-fg-3">
            While held, the node refuses any update from the interface (an upload too), whoever asks; the install
            command run on the node itself is not held.
        </p>
        <ConfirmDialog
            v-if="lifting"
            :title="`Lift the hold on ${nodeName}'s updates?`"
            :body="`A superadmin can then update ${nodeName} from the fleet page or Node software. Lifting is recorded with your name.`"
            confirm-label="Lift the hold"
            @confirm="lift"
            @cancel="lifting = false"
        />
    </div>
</template>
